import React, { useCallback, useRef } from 'react';
import BlogPrimarySection from '../../containers/sections/blog-primary-section/blog-primary-section';
import ArticlesListSection from '../../containers/sections/articles-list-section/articles-list-section';
import { IArticle } from './article-page';
import { getArticles } from '../api/get-articles';
import { environment } from '../../environment';

function getHasMoreByArticlesCount(articles: IArticle[]) {
  return articles.length === 0 ? true : articles.length % PER_PAGE === 0;
}

const PER_PAGE = environment.articlesPerPage;

interface IBlogPrimaryPageStoryblokComponentBlok {
  title: string;
  article_categories: string[];
  articleBloks: IArticle[];
  full_slug: string;
}

export function BlogPrimaryPageStoryblokComponent({ blok }: { blok: IBlogPrimaryPageStoryblokComponentBlok }) {
  const filtersByCategories = ['All', ...blok.article_categories];

  const [activeFilter, setActiveFilter] = React.useState('All');

  const [articles, setArticles] = React.useState<IArticle[]>(blok.articleBloks);

  const articlesByCategories = useRef<any>({
    All: blok.articleBloks,
  });

  const [currentPage, setCurrentPage] = React.useState(Math.floor(blok.articleBloks.length / PER_PAGE));
  const [isLoading, setIsLoading] = React.useState(false);
  const [hasMore, setHasMore] = React.useState(getHasMoreByArticlesCount(blok.articleBloks));

  const observer = useRef<IntersectionObserver | null>(null);

  const filterArticles = (category: string) => {
    if (articlesByCategories.current[category] === undefined) {
      articlesByCategories.current[category] = [];
    }

    setActiveFilter(category);
    setArticles(articlesByCategories.current[category]);
    setCurrentPage(Math.floor(articlesByCategories.current[category].length / PER_PAGE));

    setHasMore(getHasMoreByArticlesCount(articlesByCategories.current[category]));
  };

  const fetchMoreArticles = useCallback(async () => {
    if (isLoading || !hasMore) return;

    setIsLoading(true);
    try {
      const newArticles = await getArticles({
        blogSlug: blok.full_slug.split('/')[0],
        categories: activeFilter === 'All' ? blok.article_categories : [activeFilter],
        limit: PER_PAGE,
        page: currentPage + 1,
      });

      if (newArticles.length === 0) {
        setHasMore(false);
      } else {
        setArticles((prev) => [...prev, ...newArticles]);
        setCurrentPage((prevPage) => prevPage + 1);

        articlesByCategories.current[activeFilter] = [...articlesByCategories.current[activeFilter], ...newArticles];
      }
    } catch (error) {
      console.error('Error fetching more articles:', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, isLoading, hasMore, activeFilter, blok]);

  // Infinite scroll observer
  const lastArticleElementRef = useCallback(
    (node: HTMLElement | null) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          fetchMoreArticles();
        }
      });

      if (node) observer.current.observe(node);
    },
    [isLoading, hasMore, fetchMoreArticles]
  );

  const primaryArticle = articles[0];

  return (
    <>
      <BlogPrimarySection
        title={blok.title}
        filters={filtersByCategories}
        primaryArticle={
          primaryArticle
            ? {
                timeToRead: primaryArticle.time_to_read,
                category: primaryArticle.category,
                image: {
                  src: primaryArticle.primary_image?.filename,
                  alt: primaryArticle.primary_image?.alt,
                },
                title: primaryArticle.title,
                description: primaryArticle.short_description,
                link: `/${primaryArticle.full_slug}`,
              }
            : undefined
        }
        onFilterChange={filterArticles}
      />

      {articles.length > 0 && (
        <ArticlesListSection
          articles={articles.map((article) => ({
            title: article.title,
            description: article.short_description,
            createdAt: article.created_at,
            category: article.category,
            timeToRead: article.time_to_read,
            image: { src: article.primary_image?.filename, alt: article.primary_image?.alt },
            link: `/${article.full_slug}`,
          }))}
        />
      )}

      <div ref={lastArticleElementRef} />
    </>
  );
}
